import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card"; // Assuming you have a Card component
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { getListBerkasPenerimaan } from "@/services/getData";
import { DataBerkas, SysBerkas } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { LucideAlertCircle } from "lucide-react";
import { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoCloudUploadOutline, IoFolderOpenOutline } from "react-icons/io5";
import { LuDownload } from "react-icons/lu";
import Form from "./Form";

export default function UnggahBerkas({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const { timePassedActive } = useTimePeriode();
  const { isValidPendaftaran } = useDataPendaftaran();

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [showUpload, setShowUpload] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SysBerkas | null>(null);
  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  // Helper for file icon based on format (logic assumed, can be adjusted)
  // const getFileIcon = (format: string) => {
  //   if (format?.toLowerCase().includes("pdf"))
  //     return <FaFilePdf className="text-red-500" />;
  //   if (format?.toLowerCase().includes("jpg") || format?.includes("png"))
  //     return <FaImage className="text-blue-500" />;
  //   return <FaRegFile className="text-gray-500" />;
  // };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          Documents
        </h1>
        <p className="text-sm text-muted-foreground">
          Upload all required documents below.
        </p>
      </div>

      {isPending ? (
        <div className="space-y-4">
          {Array(3)
            .fill(null)
            .map((_, index) => (
              <SkeletonRow key={index} className="h-16 w-full rounded-lg" />
            ))}
        </div>
      ) : !data?.listSysBerkas || data?.listSysBerkas?.length === 0 ? (
        <Card className="border-dashed bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-green-100 p-4 rounded-full mb-3 dark:bg-green-900/30">
              <FaCheckCircle className="text-green-600 text-3xl dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              No Documents Required
            </h2>
            <p className="text-muted-foreground text-sm max-w-sm mt-1">
              This study program does not require any document uploads. You may
              proceed to the next step.
            </p>
          </div>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50/50 border-b border-gray-100 dark:bg-gray-900/50 dark:text-gray-400 dark:border-gray-800">
                <tr>
                  <th className="px-6 py-4 font-medium w-[50px] text-center">
                    #
                  </th>
                  <th className="px-6 py-4 font-medium min-w-[250px]">
                    Document Name
                  </th>
                  {/* <th className="px-6 py-4 font-medium">Requirements</th> */}
                  <th className="px-6 py-4 font-medium text-center">Status</th>
                  <th className="px-6 py-4 font-medium text-center w-[150px]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.listSysBerkas.map((row: SysBerkas, index: number) => {
                  const berkasSiswa = data?.listDataBerkasSiswa.find(
                    (berkas: DataBerkas) =>
                      berkas?.id_sys_berkas == String(row.id_sys_berkas),
                  );
                  const isUploaded = !!berkasSiswa;

                  return (
                    <tr
                      key={row.id_sys_berkas}
                      className="bg-white hover:bg-slate-50 transition-colors dark:bg-slate-950 dark:hover:bg-slate-900/50"
                    >
                      <td className="px-6 py-4 text-gray-500 text-center font-medium">
                        {index + 1}
                      </td>

                      {/* Document Name Column */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                            {row.keterangan_berkas}
                            {row.is_wajib && (
                              <span
                                className="text-red-500 ml-1"
                                title="Required"
                              >
                                *
                              </span>
                            )}
                          </span>

                          {/* Template Download Link */}
                          {row.url_template_berkas && (
                            <button
                              onClick={() =>
                                window.open(
                                  `${row.url_template_berkas}?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                                  "_blank",
                                )
                              }
                              className="flex items-center gap-1.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline w-fit mt-1 transition-colors"
                            >
                              <LuDownload size={12} /> Download Template
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Requirements Column */}
                      {/* <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed max-w-[300px]">
                            {row.keterangan_berkas ||
                              row.jenis_berkas?.keterangan ||
                              "-"}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-[10px] h-5 gap-1 font-normal text-gray-500 border-gray-200 dark:border-gray-700"
                            >
                              {getFileIcon("pdf")} PDF/JPG
                            </Badge>
                          </div>
                        </div>
                      </td> */}

                      {/* Status Column */}
                      <td className="px-6 py-4 text-center">
                        <div
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                            isUploaded
                              ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                              : "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700"
                          }`}
                        >
                          {isUploaded ? (
                            <FaCheckCircle size={12} />
                          ) : (
                            <LucideAlertCircle size={12} />
                          )}
                          {isUploaded ? "Uploaded" : "Pending"}
                        </div>
                      </td>

                      {/* Action Column */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {isUploaded && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 rounded-full"
                              onClick={() => {
                                setShowPdf(true);
                                setSelectedRow(row);
                                setSelectedBerkas(berkasSiswa || null);
                              }}
                              title="View Document"
                            >
                              <IoFolderOpenOutline size={18} />
                            </Button>
                          )}

                          {!isValidPendaftaran &&
                            !timePassedActive?.waktu_selesai_daftar_valid_selesai && (
                              <Button
                                size="sm"
                                variant={isUploaded ? "outline" : "default"}
                                className={`h-8 text-xs px-3 gap-2 shadow-sm ${
                                  !isUploaded
                                    ? "bg-blue-600 hover:bg-blue-700 text-white border-transparent"
                                    : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                                }`}
                                onClick={() => {
                                  setShowUpload(true);
                                  setSelectedRow(row);
                                }}
                              >
                                {isUploaded ? (
                                  "Change"
                                ) : (
                                  <>
                                    <IoCloudUploadOutline size={14} /> Upload
                                  </>
                                )}
                              </Button>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL UPLOAD */}
      <Modal show={showUpload} onClose={() => setShowUpload(false)}>
        <Form
          method="POST"
          selectedRow={selectedRow}
          closeModal={() => setShowUpload(false)}
          idDataPendaftaran={idDataPendaftaran}
          selectedBerkas={selectedBerkas}
        />
      </Modal>

      {/* MODAL PREVIEW PDF */}
      <Modal
        show={showPdf}
        onClose={() => {
          setShowPdf(false);
          // Optional: clear selected setelah delay animasi
          setTimeout(() => setSelectedBerkas(null), 300);
        }}
        className="lg:w-[60vw] w-[95vw]"
      >
        <div className="flex flex-col gap-4 p-2">
          <div>
            <h1 className="font-bold text-lg text-slate-900 dark:text-white">
              {selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
            </h1>
            {selectedBerkas?.nomor && (
              <Badge className="mt-2 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300">
                Nomor: {selectedBerkas?.nomor}
              </Badge>
            )}
          </div>

          {/* Panggil komponen PdfViewer */}
          {showPdf && selectedBerkas?.path_berkas && (
            <PdfViewer url={selectedBerkas.path_berkas} />
          )}
        </div>
      </Modal>
    </div>
  );
}
